import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Typography, Tag, InputNumber, App, Collapse, Grid } from "antd";

const { useBreakpoint } = Grid;
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  fetchOpeningBalancesListApi,
  saveOpeningBalancesApi,
} from "../../../apiservice/accounts";
import { getAccessType } from "../../../lib/functions";
import { getAccountType } from "../../../lib/accountTypes";

const { Title } = Typography;

// acc_type_label on individual rows is unreliable (null for most default-seeded
// accounts) — reuse the same acc_type_id -> label lookup CreateAccountForm.js
// uses for the Account Type dropdown instead, so section headers are always
// populated. debitor/creditor aren't in that dropdown (they come from the
// Customer/Supplier forms), so those fall back to the row's own label/type.
const GROUP_LABEL_BY_TYPE_ID = getAccountType().reduce((map, t) => {
  map[t.acc_type_id] = t.label;
  return map;
}, {});

const resolveGroupLabel = (row) =>
  GROUP_LABEL_BY_TYPE_ID[row.acc_type_id] || row.acc_type_label || row.acc_type_id || "Other";

const OpeningBalances = () => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [access_type, setAccessType] = useState("");
  const [activeKeys, setActiveKeys] = useState([]);

  useEffect(() => {
    setAccessType(getAccessType("account_entry"));
  }, []);

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await fetchOpeningBalancesListApi();
      const nextRows = (res.data.data || []).map((a) => ({
        acc_id: a.acc_id,
        acc_name: a.acc_name,
        acc_type_id: a.acc_type_id,
        acc_type_label: a.acc_type_label,
        is_system_account: !!a.is_system_account,
        debit: Number(a.debit) || 0,
        credit: Number(a.credit) || 0,
      }));
      setRows(nextRows);
      // Expand every section by default (same accounts visible as before grouping existed).
      setActiveKeys([...new Set(nextRows.map(resolveGroupLabel))]);
    } catch (e) {
      modal.warning({ title: "Failed to load accounts" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const updateRow = (acc_id, field, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.acc_id === acc_id
          ? {
              ...r,
              [field]: Number(value) || 0,
              // Clear the opposite field — an account normally carries only one side
              [field === "debit" ? "credit" : "debit"]: 0,
            }
          : r
      )
    );
  };

  const totals = useMemo(() => {
    const totalDebit = rows.reduce((s, r) => s + (Number(r.debit) || 0), 0);
    const totalCredit = rows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    return { totalDebit, totalCredit, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
  }, [rows]);

  // Purely a display grouping — every row still lives in the single `rows` array
  // above, and totals/handleSave still operate on the full list, so the
  // debit=credit validation always sees every classified account together.
  const groupedRows = useMemo(() => {
    const order = [];
    const byLabel = new Map();
    rows.forEach((row) => {
      const label = resolveGroupLabel(row);
      if (!byLabel.has(label)) {
        byLabel.set(label, []);
        order.push(label);
      }
      byLabel.get(label).push(row);
    });
    return order.map((label) => ({ label, rows: byLabel.get(label) }));
  }, [rows]);

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = {
        rows: rows.map((r) => ({
          acc_id: r.acc_id,
          debit: r.debit || 0,
          credit: r.credit || 0,
        })),
      };
      const res = await saveOpeningBalancesApi(payload);
      if (res.data?.error) {
        modal.warning({ title: res.data.msg || "Save failed" });
        return;
      }
      modal.success({ title: res.data?.msg || "Opening balances saved successfully" });
      await loadRows();
    } catch (e) {
      modal.warning({ title: e.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!totals.balanced) {
      const diff = Math.abs(totals.totalDebit - totals.totalCredit).toFixed(2);
      modal.confirm({
        title: "Debits and credits don't match",
        content: `This batch is off by ${diff}. The difference will appear as Difference in Opening until resolved. Continue anyway?`,
        okText: "Continue",
        cancelText: "Go back",
        onOk: doSave,
      });
      return;
    }
    await doSave();
  };

  const columns = [
    {
      title: "No.",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Account Name",
      dataIndex: "acc_name",
      render: (val, record) => (
        <>
          {val}
          {record.is_system_account ? (
            <Tag color="default" style={{ marginLeft: 8 }}>
              System
            </Tag>
          ) : null}
        </>
      ),
    },
    {
      title: "Account Type",
      dataIndex: "acc_type_label",
    },
    {
      title: "Debit",
      dataIndex: "debit",
      align: "right",
      width: screens.md ? undefined : 120,
      render: (val, record) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          value={val || null}
          onChange={(v) => updateRow(record.acc_id, "debit", v)}
        />
      ),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      align: "right",
      width: screens.md ? undefined : 120,
      render: (val, record) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          value={val || null}
          onChange={(v) => updateRow(record.acc_id, "credit", v)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="opening-balances-back-button"
        />
        <Title
          level={2}
          style={{ margin: 0, wordBreak: "break-word", fontSize: screens.md ? undefined : "22px" }}
        >
          Opening Balances
        </Title>
      </div>

      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        style={{ marginBottom: 16 }}
        items={groupedRows.map(({ label, rows: groupRows }) => ({
          key: label,
          label: `${label} (${groupRows.length})`,
          children: (
            <Table
              columns={columns}
              dataSource={groupRows}
              rowKey="acc_id"
              loading={loading}
              pagination={false}
              bordered
              scroll={{ x: "max-content" }}
              size="small"
            />
          ),
        }))}
      />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: screens.md ? "flex-end" : "flex-start",
          gap: screens.md ? 32 : 16,
          marginBottom: 16,
          color: totals.balanced ? undefined : "#ff4d4f",
        }}
      >
        <strong>Total Debit: {totals.totalDebit.toFixed(2)}</strong>
        <strong>Total Credit: {totals.totalCredit.toFixed(2)}</strong>
        {!totals.balanced && <strong>Not balanced</strong>}
      </div>

      <div style={{ display: "flex", gap: 16 }}>
        <Button onClick={() => navigate(-1)}>Back</Button>
        {access_type === "WRITE" && (
          <Button
            type="primary"
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
};

export default OpeningBalances;
